import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/auth'
import { hasRole, getAllRoles } from '../lib/roleUtils'
import { UserRole } from '../types'
import { supabase } from '../lib/supabase'
import {
  Truck, Wrench, Package, Building2, Loader2, CheckCircle2,
  ChevronRight, AlertCircle, MapPin, Phone, Mail, CreditCard,
  Upload, FileText, Shield, User, Building, X, Home
} from 'lucide-react'

export default function UnifiedOnboarding() {
  const { profile, signOut } = useAuth()
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const [autoSaving, setAutoSaving] = useState(false)
  const [showResetModal, setShowResetModal] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Refs for focus on validation error
  const fieldRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const userRoles = getAllRoles(profile)

  // Tower onboarding fields
  const [towerData, setTowerData] = useState({
    truck_type: '',
    truck_plate: '',
    truck_year: '',
    truck_insurance_provider: '',
    truck_insurance_policy: '',
    truck_insurance_expiry: '',
  })

  // Document uploads
  const [documents, setDocuments] = useState<Record<string, string>>({})
  const [uploadingDoc, setUploadingDoc] = useState<string | null>(null)

  // Parts vendor onboarding fields
  const [vendorData, setVendorData] = useState({
    business_name: '',
    business_registration: '',
    tax_pin: '',
    business_address: '',
    business_phone: '',
    business_email: '',
  })

  // Garage onboarding fields
  const [garageData, setGarageData] = useState({
    business_name: '',
    business_registration: '',
    tax_pin: '',
    business_address: '',
    business_phone: '',
    business_email: '',
  })

  // General fields (for all roles)
  const [generalData, setGeneralData] = useState({
    phone: profile?.phone || '',
    physical_address: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
  })

  // Load saved progress from localStorage
  useEffect(() => {
    const savedProgress = localStorage.getItem(`onboarding_progress_${profile?.id}`)
    if (savedProgress) {
      try {
        const progress = JSON.parse(savedProgress)
        if (progress.generalData) setGeneralData(progress.generalData)
        if (progress.towerData) setTowerData(progress.towerData)
        if (progress.vendorData) setVendorData(progress.vendorData)
        if (progress.garageData) setGarageData(progress.garageData)
        if (progress.documents) setDocuments(progress.documents)
        if (progress.step && progress.step > 1) setStep(progress.step)
      } catch (e) {
        console.error('Failed to load saved progress:', e)
      }
    }
  }, [profile?.id])

  // Auto-save progress to localStorage whenever data changes
  useEffect(() => {
    if (profile?.id) {
      setAutoSaving(true)
      const progress = {
        step,
        generalData,
        towerData,
        vendorData,
        garageData,
        documents,
      }
      localStorage.setItem(`onboarding_progress_${profile.id}`, JSON.stringify(progress))
      setTimeout(() => setAutoSaving(false), 500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, generalData, towerData, vendorData, garageData, documents, profile?.id])

  // Clear saved progress after successful submission
  const clearSavedProgress = () => {
    if (profile?.id) {
      localStorage.removeItem(`onboarding_progress_${profile.id}`)
    }
  }

  // Reset onboarding and start fresh
  const handleReset = () => {
    setShowResetModal(true)
  }

  const confirmReset = () => {
    clearSavedProgress()
    setStep(1)
    setGeneralData({
      phone: profile?.phone || '',
      physical_address: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
    })
    setTowerData({
      truck_type: '',
      truck_plate: '',
      truck_year: '',
      truck_insurance_provider: '',
      truck_insurance_policy: '',
      truck_insurance_expiry: '',
    })
    setVendorData({
      business_name: '',
      business_registration: '',
      tax_pin: '',
      business_address: '',
      business_phone: '',
      business_email: '',
    })
    setGarageData({
      business_name: '',
      business_registration: '',
      tax_pin: '',
      business_address: '',
      business_phone: '',
      business_email: '',
    })
    setDocuments({})
    setShowResetModal(false)
  }

  const handleDocumentUpload = async (docType: string, file: File) => {
    setUploadingDoc(docType)
    try {
      // Convert to base64
      const reader = new FileReader()
      reader.onload = () => {
        const base64 = reader.result as string
        setDocuments(prev => ({ ...prev, [docType]: base64 }))
        setUploadingDoc(null)
      }
      reader.onerror = () => {
        alert('Failed to read file. Please try again.')
        setUploadingDoc(null)
      }
      reader.readAsDataURL(file)
    } catch (error) {
      console.error('Upload failed:', error)
      alert('Failed to upload document. Please try again.')
      setUploadingDoc(null)
    }
  }

  const totalSteps = 2 + (hasRole(profile, 'tower') ? 1 : 0) + (hasRole(profile, 'parts_vendor') ? 1 : 0) + (hasRole(profile, 'garage') ? 1 : 0) + 1 // +1 for confirmation step

  // Calculate step numbers for each section
  const towerStep = hasRole(profile, 'tower') ? 2 : null
  const vendorStep = towerStep ? 3 : (hasRole(profile, 'parts_vendor') ? 2 : null)
  const garageStep = vendorStep ? vendorStep + 1 : (hasRole(profile, 'garage') ? 2 : null)
  const confirmationStep = totalSteps

  const handleNext = () => {
    setError('')
    setFieldErrors({})

    // Validate Step 1: General Information
    if (step === 1) {
      const errors: Record<string, string> = {}
      if (!generalData.phone) errors.phone = 'Required'
      if (!generalData.physical_address) errors.physical_address = 'Required'
      if (!generalData.emergency_contact_name) errors.emergency_contact_name = 'Required'
      if (!generalData.emergency_contact_phone) errors.emergency_contact_phone = 'Required'

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors)
        // Focus on first error field
        const firstError = Object.keys(errors)[0]
        setTimeout(() => {
          fieldRefs.current[firstError]?.focus()
          fieldRefs.current[firstError]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 100)
        return
      }
    }

    // Validate Step 2: Tower Information
    if (step === towerStep) {
      const errors: Record<string, string> = {}
      if (!towerData.truck_type) errors.truck_type = 'Required'
      if (!towerData.truck_plate) errors.truck_plate = 'Required'
      if (!towerData.truck_year) errors.truck_year = 'Required'
      if (!towerData.truck_insurance_provider) errors.truck_insurance_provider = 'Required'
      if (!towerData.truck_insurance_policy) errors.truck_insurance_policy = 'Required'
      if (!towerData.truck_insurance_expiry) errors.truck_insurance_expiry = 'Required'

      // Required documents for tower
      const requiredDocs = ['roadworthiness_cert', 'insurance_cert', 'driving_license', 'commercial_towing_license']
      const missingDocs = requiredDocs.filter(doc => !documents[doc])
      if (missingDocs.length > 0) {
        errors.documents = 'All required documents must be uploaded'
      }

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors)
        // Focus on first error field
        const firstError = Object.keys(errors)[0]
        setTimeout(() => {
          if (firstError === 'documents') {
            // Scroll to documents section
            const docSection = document.querySelector('[data-doc-section="tower"]')
            docSection?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          } else {
            fieldRefs.current[firstError]?.focus()
            fieldRefs.current[firstError]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }, 100)
        return
      }
    }

    // Validate Step: Parts Vendor Information
    if (step === vendorStep) {
      const errors: Record<string, string> = {}
      if (!vendorData.business_name) errors.business_name = 'Required'
      if (!vendorData.business_registration) errors.business_registration = 'Required'
      if (!vendorData.tax_pin) errors.tax_pin = 'Required'
      if (!vendorData.business_phone) errors.business_phone = 'Required'

      // Required documents for vendor
      const requiredDocs = ['business_permit', 'business_registration', 'tax_pin_cert', 'id_document']
      const missingDocs = requiredDocs.filter(doc => !documents[doc])
      if (missingDocs.length > 0) {
        errors.documents = 'All required documents must be uploaded'
      }

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors)
        // Focus on first error field
        const firstError = Object.keys(errors)[0]
        setTimeout(() => {
          if (firstError === 'documents') {
            // Scroll to documents section
            const docSection = document.querySelector('[data-doc-section="vendor"]')
            docSection?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          } else {
            fieldRefs.current[firstError]?.focus()
            fieldRefs.current[firstError]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }, 100)
        return
      }
    }

    // Validate Step: Garage Information
    if (step === garageStep) {
      const errors: Record<string, string> = {}
      if (!garageData.business_name) errors.business_name = 'Required'
      if (!garageData.business_registration) errors.business_registration = 'Required'
      if (!garageData.tax_pin) errors.tax_pin = 'Required'
      if (!garageData.business_phone) errors.business_phone = 'Required'

      // Required documents for garage
      const requiredDocs = ['garage_business_permit', 'garage_business_registration', 'garage_tax_pin_cert', 'garage_id_document']
      const missingDocs = requiredDocs.filter(doc => !documents[doc])
      if (missingDocs.length > 0) {
        errors.documents = 'All required documents must be uploaded'
      }

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors)
        // Focus on first error field
        const firstError = Object.keys(errors)[0]
        setTimeout(() => {
          if (firstError === 'documents') {
            // Scroll to documents section
            const docSection = document.querySelector('[data-doc-section="garage"]')
            docSection?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          } else {
            fieldRefs.current[firstError]?.focus()
            fieldRefs.current[firstError]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }, 100)
        return
      }
    }

    if (step < totalSteps) {
      setStep(step + 1)
    }
  }

  const handleBack = () => {
    setError('')
    setFieldErrors({})
    if (step > 1) {
      setStep(step - 1)
    }
  }

  const handleSubmit = async () => {
    setLoading(true)
    setError('')

    try {
      // Convert numeric fields to integers
      const towerDataNumeric = {
        ...towerData,
        truck_year: towerData.truck_year ? parseInt(towerData.truck_year) : null,
      }

      // Update profile with all onboarding data
      const updates: any = {
        onboarding_completed: true,
        verification_status: 'pending',
        phone: generalData.phone,
        physical_address: generalData.physical_address,
        emergency_contact_name: generalData.emergency_contact_name,
        emergency_contact_phone: generalData.emergency_contact_phone,
        documents: documents,
      }

      // Add tower-specific data
      if (hasRole(profile, 'tower')) {
        Object.assign(updates, towerDataNumeric)
      }

      // Add vendor-specific data
      if (hasRole(profile, 'parts_vendor')) {
        Object.assign(updates, vendorData)
      }

      // Add garage-specific data
      if (hasRole(profile, 'garage')) {
        Object.assign(updates, garageData)
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', profile.id)

      if (updateError) throw updateError

      // Ensure all roles are saved to user_roles table
      const allRoles = getAllRoles(profile)
      if (allRoles.length > 1) {
        // Delete existing roles for this user
        await supabase.from('user_roles').delete().eq('user_id', profile.id)
        // Insert all roles
        const { error: rolesError } = await supabase.from('user_roles').insert(
          allRoles.map(r => ({ user_id: profile.id, role: r }))
        )
        if (rolesError) {
          console.error('Failed to save user roles:', rolesError)
        }
      }

      // Clear saved progress after successful submission
      clearSavedProgress()

      setSaved(true)
      // Show success modal - user clicks button to go to dashboard (will reload page to refresh profile)
    } catch (e: any) {
      setError(e.message || 'Failed to complete onboarding')
    } finally {
      setLoading(false)
    }
  }

  if (!profile) return null

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '2rem' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
            <div>
              <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.5rem' }}>
                Complete Your Profile
              </h1>
              {autoSaving && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: 'var(--green)', marginBottom: '0.5rem' }}>
                  <Loader2 size={12} className="spin" />
                  Auto-saving...
                </div>
              )}
            </div>
            <button
              onClick={async () => { await signOut(); navigate('/home'); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: 'white',
                border: '1px solid var(--border)',
                borderRadius: '0.5rem',
                color: 'var(--muted)',
                cursor: 'pointer',
                fontSize: '0.875rem',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--bg)'
                e.currentTarget.style.borderColor = 'var(--text)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white'
                e.currentTarget.style.borderColor = 'var(--border)'
              }}
            >
              <Home size={16} />
              Go to Home
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1.5rem' }}>
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: '4px',
                  borderRadius: '2px',
                  background: i + 1 <= step ? 'var(--red)' : 'var(--border)',
                  transition: 'background 0.3s'
                }}
              />
            ))}
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '0.25rem' }}>
            Step {step} of {totalSteps}
          </div>
        </div>

        {error && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '1rem', background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid var(--red)', borderRadius: '0.5rem',
            color: 'var(--red)', marginBottom: '1.5rem'
          }}>
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {saved && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '1rem',
              padding: '2rem',
              maxWidth: '500px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'rgba(34, 197, 94, 0.1)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <CheckCircle2 size={40} style={{ color: 'var(--green)' }} />
              </div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.5rem' }}>
                Profile Completed!
              </h2>
              <p style={{ fontSize: '0.95rem', color: 'var(--muted)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                Your profile has been submitted for approval. An admin will review your information and approve your account. Once approved, you will have full access to all your role dashboards.
              </p>
              <button
                onClick={() => navigate('/')}
                style={{
                  width: '100%',
                  padding: '0.875rem 1.5rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  background: 'var(--red)',
                  color: 'white',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#dc2626'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--red)'
                }}
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        )}

        {showResetModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '1rem',
              padding: '2rem',
              maxWidth: '500px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'rgba(220, 38, 38, 0.1)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <AlertCircle size={40} style={{ color: 'var(--red)' }} />
              </div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.5rem' }}>
                Reset Onboarding?
              </h2>
              <p style={{ fontSize: '0.95rem', color: 'var(--muted)', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                Are you sure you want to clear all your progress and start over? This cannot be undone. All your entered information will be lost.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  onClick={() => setShowResetModal(false)}
                  style={{
                    flex: 1,
                    padding: '0.875rem 1.5rem',
                    borderRadius: '0.5rem',
                    border: '1px solid var(--border)',
                    background: 'white',
                    color: 'var(--text)',
                    fontSize: '1rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--bg)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmReset}
                  style={{
                    flex: 1,
                    padding: '0.875rem 1.5rem',
                    borderRadius: '0.5rem',
                    border: 'none',
                    background: 'var(--red)',
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#dc2626'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--red)'
                  }}
                >
                  Yes, Reset
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 1: General Information */}
        {step === 1 && (
          <div style={{ background: 'white', borderRadius: '1rem', padding: '2rem', border: '1px solid var(--border)' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <User size={24} /> General Information
            </h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '1.5rem' }}>
              Contact information that applies to all your roles
            </p>

            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                  Phone Number <span style={{ color: 'var(--red)' }}>*</span>
                </label>
                <input
                  ref={el => fieldRefs.current.phone = el}
                  type="text"
                  value={generalData.phone}
                  onChange={e => setGeneralData({...generalData, phone: e.target.value})}
                  placeholder="+254 7XX XXX XXX"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    border: fieldErrors.phone ? '1px solid var(--red)' : '1px solid var(--border)',
                    fontSize: '0.95rem'
                  }}
                />
                {fieldErrors.phone && <span style={{ color: 'var(--red)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>{fieldErrors.phone}</span>}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                  Physical Address <span style={{ color: 'var(--red)' }}>*</span>
                </label>
                <input
                  ref={el => fieldRefs.current.physical_address = el}
                  type="text"
                  value={generalData.physical_address}
                  onChange={e => setGeneralData({...generalData, physical_address: e.target.value})}
                  placeholder="Your physical address"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    border: fieldErrors.physical_address ? '1px solid var(--red)' : '1px solid var(--border)',
                    fontSize: '0.95rem'
                  }}
                />
                {fieldErrors.physical_address && <span style={{ color: 'var(--red)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>{fieldErrors.physical_address}</span>}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                    Emergency Contact Name <span style={{ color: 'var(--red)' }}>*</span>
                  </label>
                  <input
                    ref={el => fieldRefs.current.emergency_contact_name = el}
                    type="text"
                    value={generalData.emergency_contact_name}
                    onChange={e => setGeneralData({...generalData, emergency_contact_name: e.target.value})}
                    placeholder="Contact person name"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: fieldErrors.emergency_contact_name ? '1px solid var(--red)' : '1px solid var(--border)',
                      fontSize: '0.95rem'
                    }}
                  />
                  {fieldErrors.emergency_contact_name && <span style={{ color: 'var(--red)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>{fieldErrors.emergency_contact_name}</span>}
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                    Emergency Contact Phone <span style={{ color: 'var(--red)' }}>*</span>
                  </label>
                  <input
                    ref={el => fieldRefs.current.emergency_contact_phone = el}
                    type="text"
                    value={generalData.emergency_contact_phone}
                    onChange={e => setGeneralData({...generalData, emergency_contact_phone: e.target.value})}
                    placeholder="+254 7XX XXX XXX"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: fieldErrors.emergency_contact_phone ? '1px solid var(--red)' : '1px solid var(--border)',
                      fontSize: '0.95rem'
                    }}
                  />
                  {fieldErrors.emergency_contact_phone && <span style={{ color: 'var(--red)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>{fieldErrors.emergency_contact_phone}</span>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Tower Information (if tower role) */}
        {step === 2 && hasRole(profile, 'tower') && (
          <div style={{ background: 'white', borderRadius: '1rem', padding: '2rem', border: '1px solid var(--border)' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Truck size={24} /> Tower Information
            </h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '1.5rem' }}>
              Your towing vehicle and licensing details
            </p>

            <div style={{ display: 'grid', gap: '1rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                    Truck Type <span style={{ color: 'var(--red)' }}>*</span>
                  </label>
                  <input
                    ref={el => fieldRefs.current.truck_type = el}
                    type="text"
                    value={towerData.truck_type}
                    onChange={e => setTowerData({...towerData, truck_type: e.target.value})}
                    placeholder="e.g. Flatbed, Rollback"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: fieldErrors.truck_type ? '1px solid var(--red)' : '1px solid var(--border)',
                      fontSize: '0.95rem'
                    }}
                  />
                  {fieldErrors.truck_type && <span style={{ color: 'var(--red)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>{fieldErrors.truck_type}</span>}
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                    Truck Plate <span style={{ color: 'var(--red)' }}>*</span>
                  </label>
                  <input
                    ref={el => fieldRefs.current.truck_plate = el}
                    type="text"
                    value={towerData.truck_plate}
                    onChange={e => setTowerData({...towerData, truck_plate: e.target.value})}
                    placeholder="KXX 123X"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: fieldErrors.truck_plate ? '1px solid var(--red)' : '1px solid var(--border)',
                      fontSize: '0.95rem'
                    }}
                  />
                  {fieldErrors.truck_plate && <span style={{ color: 'var(--red)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>{fieldErrors.truck_plate}</span>}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                  Truck Year <span style={{ color: 'var(--red)' }}>*</span>
                </label>
                <input
                  ref={el => fieldRefs.current.truck_year = el}
                  type="number"
                  value={towerData.truck_year}
                  onChange={e => setTowerData({...towerData, truck_year: e.target.value})}
                  placeholder="2020"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    border: fieldErrors.truck_year ? '1px solid var(--red)' : '1px solid var(--border)',
                    fontSize: '0.95rem'
                  }}
                />
                {fieldErrors.truck_year && <span style={{ color: 'var(--red)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>{fieldErrors.truck_year}</span>}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                    Insurance Provider <span style={{ color: 'var(--red)' }}>*</span>
                  </label>
                  <input
                    ref={el => fieldRefs.current.truck_insurance_provider = el}
                    type="text"
                    value={towerData.truck_insurance_provider}
                    onChange={e => setTowerData({...towerData, truck_insurance_provider: e.target.value})}
                    placeholder="Insurance company name"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: fieldErrors.truck_insurance_provider ? '1px solid var(--red)' : '1px solid var(--border)',
                      fontSize: '0.95rem'
                    }}
                  />
                  {fieldErrors.truck_insurance_provider && <span style={{ color: 'var(--red)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>{fieldErrors.truck_insurance_provider}</span>}
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                    Policy Number <span style={{ color: 'var(--red)' }}>*</span>
                  </label>
                  <input
                    ref={el => fieldRefs.current.truck_insurance_policy = el}
                    type="text"
                    value={towerData.truck_insurance_policy}
                    onChange={e => setTowerData({...towerData, truck_insurance_policy: e.target.value})}
                    placeholder="Policy number"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: fieldErrors.truck_insurance_policy ? '1px solid var(--red)' : '1px solid var(--border)',
                      fontSize: '0.95rem'
                    }}
                  />
                  {fieldErrors.truck_insurance_policy && <span style={{ color: 'var(--red)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>{fieldErrors.truck_insurance_policy}</span>}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                  Insurance Expiry Date <span style={{ color: 'var(--red)' }}>*</span>
                </label>
                <input
                  ref={el => fieldRefs.current.truck_insurance_expiry = el}
                  type="date"
                  value={towerData.truck_insurance_expiry}
                  onChange={e => setTowerData({...towerData, truck_insurance_expiry: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    border: fieldErrors.truck_insurance_expiry ? '1px solid var(--red)' : '1px solid var(--border)',
                    fontSize: '0.95rem'
                  }}
                />
                {fieldErrors.truck_insurance_expiry && <span style={{ color: 'var(--red)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>{fieldErrors.truck_insurance_expiry}</span>}
              </div>

              <div style={{ marginTop: '1.5rem' }} data-doc-section="tower">
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--text)' }}>
                  <FileText size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.5rem' }} />
                  Required Documents
                </h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '1rem' }}>
                  Please upload clear photos or scans of the following documents for verification
                </p>
                {fieldErrors.documents && (
                  <div style={{ color: 'var(--red)', fontSize: '0.875rem', marginBottom: '1rem', padding: '0.5rem', background: 'rgba(220, 38, 38, 0.1)', borderRadius: '0.25rem' }}>
                    {fieldErrors.documents}
                  </div>
                )}

                <div style={{ display: 'grid', gap: '1rem' }}>
                  {[
                    { key: 'roadworthiness_cert', label: 'Roadworthiness Certificate', required: true },
                    { key: 'insurance_cert', label: 'Insurance Certificate', required: true },
                    { key: 'driving_license', label: 'Driving License', required: true },
                    { key: 'commercial_towing_license', label: 'Commercial Towing License', required: true },
                    { key: 'ntsa_cert', label: 'NTSA Certificate', required: false },
                  ].map(doc => (
                    <div
                      key={doc.key}
                      style={{
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        border: (fieldErrors.documents && doc.required && !documents[doc.key]) ? '1px solid var(--red)' : (documents[doc.key] ? '1px solid var(--green)' : '1px solid var(--border)'),
                        background: (fieldErrors.documents && doc.required && !documents[doc.key]) ? 'rgba(220, 38, 38, 0.05)' : (documents[doc.key] ? 'var(--green-light)' : 'white')
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text)' }}>
                          {doc.label}
                          {doc.required && <span style={{ color: 'var(--red)', marginLeft: '0.25rem' }}>*</span>}
                        </span>
                        {documents[doc.key] && (
                          <span style={{ fontSize: '0.75rem', color: 'var(--green)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <CheckCircle2 size={14} /> Uploaded
                          </span>
                        )}
                      </div>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        border: '1px dashed var(--border)',
                        background: 'var(--bg)',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        color: 'var(--text)',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--border)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg)'}
                      >
                        {uploadingDoc === doc.key ? (
                          <>
                            <Loader2 size={16} className="spin" /> Uploading...
                          </>
                        ) : documents[doc.key] ? (
                          <>
                            <Upload size={16} /> Replace Document
                          </>
                        ) : (
                          <>
                            <Upload size={16} /> Upload Document
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) handleDocumentUpload(doc.key, file)
                          }}
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Parts Vendor Information (if parts_vendor role) */}
        {step === vendorStep && hasRole(profile, 'parts_vendor') && (
          <div style={{ background: 'white', borderRadius: '1rem', padding: '2rem', border: '1px solid var(--border)' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Package size={24} /> Parts Shop Information
            </h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '1.5rem' }}>
              Your business and payment details
            </p>

            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                  Business Name <span style={{ color: 'var(--red)' }}>*</span>
                </label>
                <input
                  ref={el => fieldRefs.current.business_name = el}
                  type="text"
                  value={vendorData.business_name}
                  onChange={e => setVendorData({...vendorData, business_name: e.target.value})}
                  placeholder="Your shop name"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    border: fieldErrors.business_name ? '1px solid var(--red)' : '1px solid var(--border)',
                    fontSize: '0.95rem'
                  }}
                />
                {fieldErrors.business_name && <span style={{ color: 'var(--red)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>{fieldErrors.business_name}</span>}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                    Business Registration Number <span style={{ color: 'var(--red)' }}>*</span>
                  </label>
                  <input
                    ref={el => fieldRefs.current.business_registration = el}
                    type="text"
                    value={vendorData.business_registration}
                    onChange={e => setVendorData({...vendorData, business_registration: e.target.value})}
                    placeholder="Reg. No."
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: fieldErrors.business_registration ? '1px solid var(--red)' : '1px solid var(--border)',
                      fontSize: '0.95rem'
                    }}
                  />
                  {fieldErrors.business_registration && <span style={{ color: 'var(--red)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>{fieldErrors.business_registration}</span>}
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                    Tax PIN <span style={{ color: 'var(--red)' }}>*</span>
                  </label>
                  <input
                    ref={el => fieldRefs.current.tax_pin = el}
                    type="text"
                    value={vendorData.tax_pin}
                    onChange={e => setVendorData({...vendorData, tax_pin: e.target.value})}
                    placeholder="A00 123456 789X"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: fieldErrors.tax_pin ? '1px solid var(--red)' : '1px solid var(--border)',
                      fontSize: '0.95rem'
                    }}
                  />
                  {fieldErrors.tax_pin && <span style={{ color: 'var(--red)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>{fieldErrors.tax_pin}</span>}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                  Business Address
                </label>
                <input
                  type="text"
                  value={vendorData.business_address}
                  onChange={e => setVendorData({...vendorData, business_address: e.target.value})}
                  placeholder="Your business location"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    border: '1px solid var(--border)',
                    fontSize: '0.95rem'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                    Business Phone <span style={{ color: 'var(--red)' }}>*</span>
                  </label>
                  <input
                    ref={el => fieldRefs.current.business_phone = el}
                    type="text"
                    value={vendorData.business_phone}
                    onChange={e => setVendorData({...vendorData, business_phone: e.target.value})}
                    placeholder="+254 7XX XXX XXX"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: fieldErrors.business_phone ? '1px solid var(--red)' : '1px solid var(--border)',
                      fontSize: '0.95rem'
                    }}
                  />
                  {fieldErrors.business_phone && <span style={{ color: 'var(--red)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>{fieldErrors.business_phone}</span>}
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                    Business Email
                  </label>
                  <input
                    type="email"
                    value={vendorData.business_email}
                    onChange={e => setVendorData({...vendorData, business_email: e.target.value})}
                    placeholder="email@business.com"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: '1px solid var(--border)',
                      fontSize: '0.95rem'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginTop: '1.5rem' }} data-doc-section="vendor">
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--text)' }}>
                  <FileText size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.5rem' }} />
                  Required Documents
                </h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '1rem' }}>
                  Please upload clear photos or scans of the following documents for verification
                </p>
                {fieldErrors.documents && (
                  <div style={{ color: 'var(--red)', fontSize: '0.875rem', marginBottom: '1rem', padding: '0.5rem', background: 'rgba(220, 38, 38, 0.1)', borderRadius: '0.25rem' }}>
                    {fieldErrors.documents}
                  </div>
                )}

                <div style={{ display: 'grid', gap: '1rem' }}>
                  {[
                    { key: 'business_permit', label: 'Business Permit', required: true },
                    { key: 'business_registration', label: 'Business Registration Certificate', required: true },
                    { key: 'tax_pin_cert', label: 'KRA Tax PIN Certificate', required: true },
                    { key: 'id_document', label: 'National ID Document', required: true },
                  ].map(doc => (
                    <div
                      key={doc.key}
                      style={{
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        border: (fieldErrors.documents && doc.required && !documents[doc.key]) ? '1px solid var(--red)' : (documents[doc.key] ? '1px solid var(--green)' : '1px solid var(--border)'),
                        background: (fieldErrors.documents && doc.required && !documents[doc.key]) ? 'rgba(220, 38, 38, 0.05)' : (documents[doc.key] ? 'var(--green-light)' : 'white')
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text)' }}>
                          {doc.label}
                          {doc.required && <span style={{ color: 'var(--red)', marginLeft: '0.25rem' }}>*</span>}
                        </span>
                        {documents[doc.key] && (
                          <span style={{ fontSize: '0.75rem', color: 'var(--green)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <CheckCircle2 size={14} /> Uploaded
                          </span>
                        )}
                      </div>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        border: '1px dashed var(--border)',
                        background: 'var(--bg)',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        color: 'var(--text)',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--border)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg)'}
                      >
                        {uploadingDoc === doc.key ? (
                          <>
                            <Loader2 size={16} className="spin" /> Uploading...
                          </>
                        ) : documents[doc.key] ? (
                          <>
                            <Upload size={16} /> Replace Document
                          </>
                        ) : (
                          <>
                            <Upload size={16} /> Upload Document
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) handleDocumentUpload(doc.key, file)
                          }}
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Garage Information (if garage role) */}
        {step === garageStep && hasRole(profile, 'garage') && (
          <div style={{ background: 'white', borderRadius: '1rem', padding: '2rem', border: '1px solid var(--border)' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Wrench size={24} /> Garage Information
            </h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '1.5rem' }}>
              Your garage business and payment details
            </p>

            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                  Business Name <span style={{ color: 'var(--red)' }}>*</span>
                </label>
                <input
                  ref={el => fieldRefs.current.business_name = el}
                  type="text"
                  value={garageData.business_name}
                  onChange={e => setGarageData({...garageData, business_name: e.target.value})}
                  placeholder="Your garage name"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    border: fieldErrors.business_name ? '1px solid var(--red)' : '1px solid var(--border)',
                    fontSize: '0.95rem'
                  }}
                />
                {fieldErrors.business_name && <span style={{ color: 'var(--red)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>{fieldErrors.business_name}</span>}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                    Business Registration Number <span style={{ color: 'var(--red)' }}>*</span>
                  </label>
                  <input
                    ref={el => fieldRefs.current.business_registration = el}
                    type="text"
                    value={garageData.business_registration}
                    onChange={e => setGarageData({...garageData, business_registration: e.target.value})}
                    placeholder="Reg. No."
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: fieldErrors.business_registration ? '1px solid var(--red)' : '1px solid var(--border)',
                      fontSize: '0.95rem'
                    }}
                  />
                  {fieldErrors.business_registration && <span style={{ color: 'var(--red)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>{fieldErrors.business_registration}</span>}
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                    Tax PIN <span style={{ color: 'var(--red)' }}>*</span>
                  </label>
                  <input
                    ref={el => fieldRefs.current.tax_pin = el}
                    type="text"
                    value={garageData.tax_pin}
                    onChange={e => setGarageData({...garageData, tax_pin: e.target.value})}
                    placeholder="A00 123456 789X"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: fieldErrors.tax_pin ? '1px solid var(--red)' : '1px solid var(--border)',
                      fontSize: '0.95rem'
                    }}
                  />
                  {fieldErrors.tax_pin && <span style={{ color: 'var(--red)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>{fieldErrors.tax_pin}</span>}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                  Business Address
                </label>
                <input
                  type="text"
                  value={garageData.business_address}
                  onChange={e => setGarageData({...garageData, business_address: e.target.value})}
                  placeholder="Your business location"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    border: '1px solid var(--border)',
                    fontSize: '0.95rem'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                    Business Phone <span style={{ color: 'var(--red)' }}>*</span>
                  </label>
                  <input
                    ref={el => fieldRefs.current.business_phone = el}
                    type="text"
                    value={garageData.business_phone}
                    onChange={e => setGarageData({...garageData, business_phone: e.target.value})}
                    placeholder="+254 7XX XXX XXX"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: fieldErrors.business_phone ? '1px solid var(--red)' : '1px solid var(--border)',
                      fontSize: '0.95rem'
                    }}
                  />
                  {fieldErrors.business_phone && <span style={{ color: 'var(--red)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>{fieldErrors.business_phone}</span>}
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.4rem', color: 'var(--text)' }}>
                    Business Email
                  </label>
                  <input
                    type="email"
                    value={garageData.business_email}
                    onChange={e => setGarageData({...garageData, business_email: e.target.value})}
                    placeholder="email@business.com"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: '1px solid var(--border)',
                      fontSize: '0.95rem'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginTop: '1.5rem' }} data-doc-section="garage">
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--text)' }}>
                  <FileText size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.5rem' }} />
                  Required Documents
                </h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '1rem' }}>
                  Please upload clear photos or scans of the following documents for verification
                </p>
                {fieldErrors.documents && (
                  <div style={{ color: 'var(--red)', fontSize: '0.875rem', marginBottom: '1rem', padding: '0.5rem', background: 'rgba(220, 38, 38, 0.1)', borderRadius: '0.25rem' }}>
                    {fieldErrors.documents}
                  </div>
                )}

                <div style={{ display: 'grid', gap: '1rem' }}>
                  {[
                    { key: 'garage_business_permit', label: 'Business Permit', required: true },
                    { key: 'garage_business_registration', label: 'Business Registration Certificate', required: true },
                    { key: 'garage_tax_pin_cert', label: 'KRA Tax PIN Certificate', required: true },
                    { key: 'garage_id_document', label: 'National ID Document', required: true },
                    { key: 'garage_trade_license', label: 'Trade License', required: false },
                  ].map(doc => (
                    <div
                      key={doc.key}
                      style={{
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        border: (fieldErrors.documents && doc.required && !documents[doc.key]) ? '1px solid var(--red)' : (documents[doc.key] ? '1px solid var(--green)' : '1px solid var(--border)'),
                        background: (fieldErrors.documents && doc.required && !documents[doc.key]) ? 'rgba(220, 38, 38, 0.05)' : (documents[doc.key] ? 'var(--green-light)' : 'white')
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text)' }}>
                          {doc.label}
                          {doc.required && <span style={{ color: 'var(--red)', marginLeft: '0.25rem' }}>*</span>}
                        </span>
                        {documents[doc.key] && (
                          <span style={{ fontSize: '0.75rem', color: 'var(--green)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            <CheckCircle2 size={14} /> Uploaded
                          </span>
                        )}
                      </div>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        border: '1px dashed var(--border)',
                        background: 'var(--bg)',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        color: 'var(--text)',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--border)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg)'}
                      >
                        {uploadingDoc === doc.key ? (
                          <>
                            <Loader2 size={16} className="spin" /> Uploading...
                          </>
                        ) : documents[doc.key] ? (
                          <>
                            <Upload size={16} /> Replace Document
                          </>
                        ) : (
                          <>
                            <Upload size={16} /> Upload Document
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) handleDocumentUpload(doc.key, file)
                          }}
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Final Step: Review & Submit */}
        {step === totalSteps && (
          <div style={{ background: 'white', borderRadius: '1rem', padding: '2rem', border: '1px solid var(--border)' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <CheckCircle2 size={24} /> Review & Submit
            </h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '1.5rem' }}>
              Review your information before submitting for approval
            </p>

            {/* Selected Roles */}
            <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'var(--bg)', borderRadius: '0.5rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Shield size={18} /> Selected Roles
              </h3>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {userRoles.map((role) => {
                  const roleColors: Record<string, { bg: string; text: string; icon: any }> = {
                    tower: { bg: '#1e40af', text: 'white', icon: Truck },
                    garage: { bg: '#ea580c', text: 'white', icon: Wrench },
                    parts_vendor: { bg: '#059669', text: 'white', icon: Package },
                    insurer: { bg: '#7c3aed', text: 'white', icon: Building2 },
                    customer: { bg: '#dc2626', text: 'white', icon: User },
                  }
                  const colors = roleColors[role] || { bg: '#6b7280', text: 'white', icon: User }
                  const Icon = colors.icon
                  const label = role === 'parts_vendor' ? 'Parts Shop' : role.charAt(0).toUpperCase() + role.slice(1)

                  return (
                    <div
                      key={role}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                        padding: '0.5rem 0.75rem',
                        background: colors.bg,
                        color: colors.text,
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      }}
                    >
                      <Icon size={16} />
                      {label}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* General Information */}
            <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'var(--bg)', borderRadius: '0.5rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <User size={18} /> General Information
              </h3>
              <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.875rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem' }}>
                  <span style={{ color: 'var(--muted)' }}>Phone:</span>
                  <span style={{ color: 'var(--text)', fontWeight: 500 }}>{generalData.phone || 'Not provided'}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem' }}>
                  <span style={{ color: 'var(--muted)' }}>Physical Address:</span>
                  <span style={{ color: 'var(--text)', fontWeight: 500 }}>{generalData.physical_address || 'Not provided'}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem' }}>
                  <span style={{ color: 'var(--muted)' }}>Emergency Contact:</span>
                  <span style={{ color: 'var(--text)', fontWeight: 500 }}>{generalData.emergency_contact_name || 'Not provided'}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem' }}>
                  <span style={{ color: 'var(--muted)' }}>Emergency Phone:</span>
                  <span style={{ color: 'var(--text)', fontWeight: 500 }}>{generalData.emergency_contact_phone || 'Not provided'}</span>
                </div>
              </div>
            </div>

            {/* Tower Information */}
            {hasRole(profile, 'tower') && (
              <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'var(--bg)', borderRadius: '0.5rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Truck size={18} /> Tower Information
                </h3>
                <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.875rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem' }}>
                    <span style={{ color: 'var(--muted)' }}>Truck Type:</span>
                    <span style={{ color: 'var(--text)', fontWeight: 500 }}>{towerData.truck_type || 'Not provided'}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem' }}>
                    <span style={{ color: 'var(--muted)' }}>Truck Plate:</span>
                    <span style={{ color: 'var(--text)', fontWeight: 500 }}>{towerData.truck_plate || 'Not provided'}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem' }}>
                    <span style={{ color: 'var(--muted)' }}>Truck Year:</span>
                    <span style={{ color: 'var(--text)', fontWeight: 500 }}>{towerData.truck_year || 'Not provided'}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem' }}>
                    <span style={{ color: 'var(--muted)' }}>Insurance Provider:</span>
                    <span style={{ color: 'var(--text)', fontWeight: 500 }}>{towerData.truck_insurance_provider || 'Not provided'}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem' }}>
                    <span style={{ color: 'var(--muted)' }}>Policy Number:</span>
                    <span style={{ color: 'var(--text)', fontWeight: 500 }}>{towerData.truck_insurance_policy || 'Not provided'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Parts Vendor Information */}
            {hasRole(profile, 'parts_vendor') && (
              <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'var(--bg)', borderRadius: '0.5rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Package size={18} /> Parts Shop Information
                </h3>
                <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.875rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem' }}>
                    <span style={{ color: 'var(--muted)' }}>Business Name:</span>
                    <span style={{ color: 'var(--text)', fontWeight: 500 }}>{vendorData.business_name || 'Not provided'}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem' }}>
                    <span style={{ color: 'var(--muted)' }}>Registration No:</span>
                    <span style={{ color: 'var(--text)', fontWeight: 500 }}>{vendorData.business_registration || 'Not provided'}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem' }}>
                    <span style={{ color: 'var(--muted)' }}>Tax PIN:</span>
                    <span style={{ color: 'var(--text)', fontWeight: 500 }}>{vendorData.tax_pin || 'Not provided'}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem' }}>
                    <span style={{ color: 'var(--muted)' }}>Business Phone:</span>
                    <span style={{ color: 'var(--text)', fontWeight: 500 }}>{vendorData.business_phone || 'Not provided'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Garage Information */}
            {hasRole(profile, 'garage') && (
              <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'var(--bg)', borderRadius: '0.5rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Wrench size={18} /> Garage Information
                </h3>
                <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.875rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem' }}>
                    <span style={{ color: 'var(--muted)' }}>Business Name:</span>
                    <span style={{ color: 'var(--text)', fontWeight: 500 }}>{garageData.business_name || 'Not provided'}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem' }}>
                    <span style={{ color: 'var(--muted)' }}>Registration No:</span>
                    <span style={{ color: 'var(--text)', fontWeight: 500 }}>{garageData.business_registration || 'Not provided'}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem' }}>
                    <span style={{ color: 'var(--muted)' }}>Tax PIN:</span>
                    <span style={{ color: 'var(--text)', fontWeight: 500 }}>{garageData.tax_pin || 'Not provided'}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0.5rem' }}>
                    <span style={{ color: 'var(--muted)' }}>Business Phone:</span>
                    <span style={{ color: 'var(--text)', fontWeight: 500 }}>{garageData.business_phone || 'Not provided'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Documents */}
            {Object.keys(documents).length > 0 && (
              <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'var(--bg)', borderRadius: '0.5rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <FileText size={18} /> Uploaded Documents
                </h3>
                <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.875rem' }}>
                  {Object.keys(documents).map((docKey) => (
                    <div key={docKey} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <CheckCircle2 size={16} style={{ color: 'var(--green)' }} />
                      <span style={{ color: 'var(--text)', fontWeight: 500 }}>
                        {docKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ padding: '1.5rem', background: 'rgba(59, 130, 246, 0.05)', borderRadius: '0.5rem', border: '1px solid rgba(59, 130, 246, 0.15)' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--text)', lineHeight: 1.5 }}>
                <strong style={{ color: 'var(--blue)' }}>Next Steps:</strong> After submitting, your account will be set to pending approval status. An admin will review your information and approve your account. Once approved, you will have full access to all your role dashboards.
              </p>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '2rem', gap: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={async () => { await signOut(); navigate('/home'); }}
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: '0.5rem',
                border: '1px solid var(--border)',
                background: 'white',
                color: 'var(--muted)',
                fontSize: '0.95rem',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--text)'
                e.currentTarget.style.color = 'var(--text)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border)'
                e.currentTarget.style.color = 'var(--muted)'
              }}
            >
              <X size={18} />
              Cancel
            </button>
            <button
              onClick={handleReset}
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: '0.5rem',
                border: '1px solid var(--red)',
                background: 'white',
                color: 'var(--red)',
                fontSize: '0.95rem',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(220, 38, 38, 0.05)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white'
              }}
            >
              Reset
            </button>
          </div>

          <div style={{ display: 'flex', gap: '1rem' }}>
            {step > 1 && (
              <button
                onClick={handleBack}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  border: '1px solid var(--border)',
                  background: 'white',
                  color: 'var(--text)',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--bg)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white'
                }}
              >
                Back
              </button>
            )}

            {step < totalSteps ? (
              <button
                onClick={handleNext}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  background: 'var(--red)',
                  color: 'white',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#dc2626'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--red)'
                }}
              >
                Next <ChevronRight size={18} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  background: loading ? 'var(--muted)' : 'var(--red)',
                  color: 'white',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (!loading) e.currentTarget.style.background = '#dc2626'
                }}
                onMouseLeave={(e) => {
                  if (!loading) e.currentTarget.style.background = 'var(--red)'
                }}
              >
                {loading ? <Loader2 size={18} className="spin" /> : 'Submit for Approval'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
